/**
 * SuitePress Theme UI
 * Dark mode toggle, scroll behaviors, scroll reveal, back-to-top
 */
(function () {
  'use strict';

  /* ── CONSTANTS ── */
  const DARK_CLASS   = 'dark';
  const STORAGE_KEY  = 'sp-theme';
  const html         = document.documentElement;

  /* ════════════════════════════════════════════════════════
     1. DARK MODE
     ════════════════════════════════════════════════════════ */

  function getSystemPreference() {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }

  function getStoredTheme() {
    try { return localStorage.getItem(STORAGE_KEY); } catch (e) { return null; }
  }

  function setStoredTheme(theme) {
    try { localStorage.setItem(STORAGE_KEY, theme); } catch (e) {}
  }

  function applyTheme(theme) {
    if (theme === 'dark') {
      html.setAttribute('data-theme', 'dark');
    } else {
      html.removeAttribute('data-theme');
    }
    updateToggleIcon(theme);
  }

  function updateToggleIcon(theme) {
    const btn = document.getElementById('sp-theme-toggle');
    if (!btn) return;
    const isDark = theme === 'dark';
    btn.setAttribute('aria-label', isDark ? 'Switch to light mode' : 'Switch to dark mode');
    btn.innerHTML = isDark ? getSunIcon() : getMoonIcon();
  }

  function getMoonIcon() {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>`;
  }

  function getSunIcon() {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>`;
  }

  function injectToggleButton() {
    if (document.getElementById('sp-theme-toggle')) return;

    const btn = document.createElement('button');
    btn.id = 'sp-theme-toggle';
    btn.className = 'sp-theme-toggle';
    btn.type = 'button';

    const currentTheme = getStoredTheme() || getSystemPreference();
    btn.setAttribute('aria-label', currentTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode');
    btn.innerHTML = currentTheme === 'dark' ? getSunIcon() : getMoonIcon();

    btn.addEventListener('click', function () {
      const current = html.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
      const next    = current === 'dark' ? 'light' : 'dark';
      setStoredTheme(next);
      applyTheme(next);
    });

    /* Insert before the search icon wrapper in header */
    const searchWrapper = document.querySelector('.suitepress-header .search-icon-wrapper');
    const headerNav     = document.querySelector('.suitepress-header .header-nav');

    if (searchWrapper) {
      searchWrapper.insertAdjacentElement('afterend', btn);
    } else if (headerNav) {
      headerNav.prepend(btn);
    }
  }

  /* ════════════════════════════════════════════════════════
     2. STICKY HEADER ON SCROLL
     ════════════════════════════════════════════════════════ */

  function initStickyHeader() {
    const header = document.querySelector('.suitepress-header');
    if (!header) return;

    let lastScroll = 0;

    function onScroll() {
      const currentScroll = window.scrollY;

      if (currentScroll > 60) {
        header.classList.add('sp-scrolled');
      } else {
        header.classList.remove('sp-scrolled');
      }

      lastScroll = currentScroll;
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ════════════════════════════════════════════════════════
     3. SCROLL REVEAL
     ════════════════════════════════════════════════════════ */

  function initScrollReveal() {
    if (!window.IntersectionObserver) return;

    /* Auto-mark eligible elements */
    const targets = [
      '.sp-blog-card',
      '.sp-addon-card',
      '.card',
      '.service-card',
      '.sp-stat-item',
      '.trust-item',
      '.sp-addon-feature-item',
      '.footer-widget-column',
    ].join(', ');

    document.querySelectorAll(targets).forEach(function (el, i) {
      el.classList.add('sp-reveal');
      /* Stagger within the same parent */
      const siblings = el.parentElement ? el.parentElement.children : [];
      const idx = Array.from(siblings).indexOf(el);
      if (idx > 0 && idx <= 4) {
        el.style.transitionDelay = (idx * 80) + 'ms';
      }
    });

    const observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('sp-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });

    document.querySelectorAll('.sp-reveal').forEach(function (el) {
      observer.observe(el);
    });
  }

  /* ════════════════════════════════════════════════════════
     4. BACK TO TOP
     ════════════════════════════════════════════════════════ */

  function initBackToTop() {
    const btn = document.createElement('button');
    btn.id = 'sp-back-to-top';
    btn.type = 'button';
    btn.setAttribute('aria-label', 'Back to top');
    btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="18 15 12 9 6 15"/></svg>`;
    document.body.appendChild(btn);

    btn.addEventListener('click', function () {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });

    window.addEventListener('scroll', function () {
      if (window.scrollY > 400) {
        btn.classList.add('visible');
      } else {
        btn.classList.remove('visible');
      }
    }, { passive: true });
  }

  /* ════════════════════════════════════════════════════════
     5. READING PROGRESS BAR
     ════════════════════════════════════════════════════════ */

  function initReadingProgress() {
    const bar = document.createElement('div');
    bar.id = 'sp-progress-bar';
    bar.setAttribute('role', 'progressbar');
    bar.setAttribute('aria-valuemin', '0');
    bar.setAttribute('aria-valuemax', '100');
    bar.setAttribute('aria-valuenow', '0');
    bar.setAttribute('aria-label', 'Page reading progress');
    document.body.prepend(bar);

    function updateProgress() {
      const el  = document.documentElement;
      const pct = el.scrollHeight - el.clientHeight > 0
        ? (el.scrollTop / (el.scrollHeight - el.clientHeight)) * 100
        : 0;
      const val = Math.min(Math.round(pct * 10) / 10, 100);
      bar.style.width = val + '%';
      bar.setAttribute('aria-valuenow', val);
    }

    window.addEventListener('scroll', updateProgress, { passive: true });
    updateProgress();
  }

  /* ════════════════════════════════════════════════════════
     6. SEARCH POPUP
     ════════════════════════════════════════════════════════ */

  function initSearchPopup() {
    var toggle  = document.querySelector('.search-toggle');
    var popup   = document.querySelector('.search-popup');
    var overlay = document.querySelector('.search-popup-overlay');
    var close   = document.querySelector('.search-close');
    var field   = document.querySelector('.search-popup .search-field');

    if (!toggle || !popup) return;

    var closeTimer = null;

    function openSearch() {
      if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
      popup.classList.remove('sp-closing');
      popup.classList.add('active');
      document.body.style.overflow = 'hidden';
      if (field) setTimeout(function () { field.focus(); }, 80);
    }

    function closeSearch() {
      popup.classList.add('sp-closing');
      document.body.style.overflow = '';
      /* wait for CSS exit animation before hiding */
      closeTimer = setTimeout(function () {
        popup.classList.remove('active', 'sp-closing');
        closeTimer = null;
      }, 220);
    }

    toggle.addEventListener('click', openSearch);
    if (close)   close.addEventListener('click', closeSearch);
    if (overlay) overlay.addEventListener('click', closeSearch);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && popup.classList.contains('active')) {
        closeSearch();
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        popup.classList.contains('active') ? closeSearch() : openSearch();
      }
    });
  }

  /* ════════════════════════════════════════════════════════
     7. MOBILE NAV CLOSE ON LINK CLICK
     ════════════════════════════════════════════════════════ */

  function initMobileNav() {
    const collapse = document.querySelector('.navbar-collapse');
    if (!collapse) return;

    document.querySelectorAll('.navbar-nav .nav-link').forEach(function (link) {
      link.addEventListener('click', function () {
        if (window.innerWidth < 1024 && collapse.classList.contains('show')) {
          /* Let Bootstrap handle the toggle */
          const toggler = document.querySelector('.navbar-toggler');
          if (toggler) toggler.click();
        }
      });
    });
  }

  /* ════════════════════════════════════════════════════════
     8. BANNER CLOSE (persistence)
     ════════════════════════════════════════════════════════ */

  function initBannerClose() {
    const closeBtn = document.querySelector('.sp-banner-close');
    const banner   = document.querySelector('.sp-top-banner');
    if (!closeBtn || !banner) return;

    const BANNER_KEY = 'sp-banner-dismissed';
    if (sessionStorage.getItem(BANNER_KEY)) {
      banner.style.display = 'none';
      return;
    }

    closeBtn.addEventListener('click', function () {
      banner.style.maxHeight = banner.scrollHeight + 'px';
      requestAnimationFrame(function () {
        banner.style.transition = 'max-height 0.4s ease, opacity 0.3s ease, padding 0.3s ease';
        banner.style.maxHeight  = '0';
        banner.style.opacity    = '0';
        banner.style.padding    = '0';
        banner.style.overflow   = 'hidden';
      });
      sessionStorage.setItem(BANNER_KEY, '1');
    });
  }

  /* ════════════════════════════════════════════════════════
     INIT
     ════════════════════════════════════════════════════════ */

  /* Apply saved/system theme before paint to avoid flash */
  (function () {
    const saved  = getStoredTheme();
    const system = getSystemPreference();
    applyTheme(saved || system);
  })();

  /* Listen for OS theme changes */
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', function (e) {
    if (!getStoredTheme()) {
      applyTheme(e.matches ? 'dark' : 'light');
    }
  });

  /* ════════════════════════════════════════════════════════
     9. TABLE OF CONTENTS (single post sidebar)
     ════════════════════════════════════════════════════════ */

  function initTableOfContents() {
    var content  = document.querySelector('.sp-entry-content');
    var sidebar  = document.querySelector('.sp-sidebar-widgets');
    if (!content || !sidebar) return;

    var headings = Array.from(content.querySelectorAll('h2'));
    if (headings.length < 1) return;

    /* Stamp IDs onto any headings that don't have one */
    headings.forEach(function (h, i) {
      if (!h.id) {
        var slug = h.textContent.trim()
          .toLowerCase()
          .replace(/[^a-z0-9\s-]/g, '')
          .trim()
          .replace(/\s+/g, '-');
        h.id = 'toc-' + i + '-' + slug;
      }
    });

    /* Build the widget */
    var widget = document.createElement('div');
    widget.id        = 'sp-toc-widget';
    widget.className = 'sp-toc-widget';

    var header = document.createElement('div');
    header.className = 'sp-toc-header';

    var title = document.createElement('h3');
    title.className   = 'sp-toc-title';
    title.textContent = 'Quick Navigation';

    var toggle = document.createElement('button');
    toggle.type      = 'button';
    toggle.className = 'sp-toc-toggle';
    toggle.setAttribute('aria-label', 'Collapse table of contents');
    toggle.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="18 15 12 9 6 15"/></svg>';

    header.appendChild(title);
    header.appendChild(toggle);

    var list = document.createElement('ol');
    list.className = 'sp-toc-list';

    headings.forEach(function (h) {
      var li = document.createElement('li');
      li.className = 'sp-toc-item';

      var a = document.createElement('a');
      a.href      = '#' + h.id;
      a.className = 'sp-toc-link';
      a.textContent = h.textContent.trim();

      a.addEventListener('click', function (e) {
        e.preventDefault();
        var offset = 80; /* account for sticky header */
        var top    = h.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top: top, behavior: 'smooth' });
        history.pushState(null, null, '#' + h.id);
      });

      li.appendChild(a);
      list.appendChild(li);
    });

    /* Collapse / expand toggle */
    var collapsed = false;
    toggle.addEventListener('click', function () {
      collapsed = !collapsed;
      list.style.display = collapsed ? 'none' : '';
      toggle.setAttribute('aria-label', collapsed ? 'Expand table of contents' : 'Collapse table of contents');
      toggle.classList.toggle('sp-toc-toggle--collapsed', collapsed);
    });

    widget.appendChild(header);
    widget.appendChild(list);

    /* Prepend before any existing sidebar widgets */
    sidebar.insertBefore(widget, sidebar.firstChild);

    /* ── Active section highlight via IntersectionObserver ── */
    if (!window.IntersectionObserver) return;

    var activeLink = null;

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var link = list.querySelector('a[href="#' + entry.target.id + '"]');
        if (!link) return;

        if (entry.isIntersecting) {
          if (activeLink) activeLink.classList.remove('sp-toc-link--active');
          link.classList.add('sp-toc-link--active');
          activeLink = link;
        }
      });
    }, { rootMargin: '-15% 0px -80% 0px', threshold: 0 });

    headings.forEach(function (h) { observer.observe(h); });
  }

  document.addEventListener('DOMContentLoaded', function () {
    // injectToggleButton(); // Toggle button hidden — re-enable when needed
    initStickyHeader();
    initScrollReveal();
    initBackToTop();
    initReadingProgress();
    initSearchPopup();
    initMobileNav();
    initBannerClose();
    initTableOfContents();
  });

})();
